/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import Colors from '@ziynx/theme/colors';
import styled from 'styled-components';
import { eventNames } from '@ziynx/analytics';
import { withAppManager, LoadingSpinner } from '@ziynx/components/utilities';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import Link from '@ziynx/components/links';
import Headline from '@ziynx/components/headlines';
import Card from '@ziynx/components/cards';
import ActionTypes from '@ziynx/action-types';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { Body } from '@ziynx/components/typography';

type History = {
  push: (pathName: string, state: Object) => void,
};

type DispatchAction = {
  type: string,
  id?: string,
  page?: number,
} => void;

const LinkContainer = styled.div`
  margin-bottom: 24px;
`;

const HeadlineContainer = styled.div`
  padding-bottom: 40px;
`;

const StyledBody = styled(Body)`
  padding-top: 4px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 32px;
`;

const ZIYNX_LOGO = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';

const CardContainer = styled.div`
  ${({ lastChild }) => !lastChild && 'padding-bottom: 64px;'};
`;

const NILApplicationScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, location } = props;
  const history: History = useHistory();
  const { id: NILPostId } = useParams();
  const dispatch: DispatchAction = useDispatch();

  const { postTitle } = location?.state;

  useEffect(() => {
    if (NILPostId) {
      dispatch({
        type: ActionTypes.NIL.applications.getAll.START,
        id: NILPostId,
        query: {
          page: 0,
        },
      });
    }
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_nil_applications,
      properties: {
        NILPostId,
      },
    });
  }, [NILPostId]);

  const applicationService = useSelector(({ NILApplications }) => ((NILApplications[NILPostId] || {})), shallowEqual);

  const isMobileSmall = viewport === 'mobileSmall';

  const _onPaginate = (inView): void => {
    if (applicationService?.isLastPage || applicationService?.fetching || !inView) return;
    const nextPage = applicationService?.page + 1;
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.scrolled_through_nil_applications,
      properties: {
        pageNumber: nextPage,
      },
    });
    dispatch({
      type: ActionTypes.NIL.applications.getAll.START,
      id: NILPostId,
      query: {
        page: nextPage,
      },
    });
  };

  const _renderLeftSlot = (): React$Node => (
    <>
      <LinkContainer>
        <Link variant="caret" onClick={() => history.push('/nil', { from: 'Applications' })} color={Colors.GrayDark}>
          Back to NIL
        </Link>
      </LinkContainer>
      <HeadlineContainer>
        <Headline
          variant="page"
          title={{
            text: postTitle || 'NIL Post',
            color: Colors.GrayVeryDark,
          }}
        />
      </HeadlineContainer>
    </>
  );

  const _renderCards = () => (applicationService?.results ?? []).map((item, index) => {
    if (!Object.keys(item).length) return null;

    const fullName = `${item?.profile?.firstName} ${item?.profile?.lastName}`;
    const videoSource = item?.video?.sources?.mp4_1080_square?.url
    || item?.video?.sources?.mp4?.url;

    const thumbnail = item?.video?.sources?.thumbnail_square?.url
    || item?.video?.sources?.thumbnail?.url;

    const thumbnailOptimized = item?.video?.sources?.thumbnail_optimized?.url || '';

    const createdAt = new Date(item?.createdAt).toLocaleDateString('en-us', { year: 'numeric', month: 'short', day: 'numeric' });

    const TALENT_RESULT = [{
      headline: {
        title: {
          text: fullName,
          onClick: () => history.push(`/discover/talent/${item?.profile?._id}`, {
            name: applicationService?.name,
            backLink: `/nil/applications/${applicationService?._id}`,
          }),
          variant: 'text',
          small: true,
          type: 'heading',
        },
        children: (
          <StyledBody
            medium
            color={Colors.GrayDark}
          >
            {createdAt}
          </StyledBody>
        ),
      },
      thumbnail: {
        uri: thumbnail || ZIYNX_LOGO,
      },
      thumbnail_optimized: {
        uri: thumbnailOptimized,
      },
      video: {
        onPlay: () => {
          dispatch({
            type: ActionTypes.analytics.logEvent.START,
            name: eventNames.played_nil_post_application_video,
            properties: {
              videoType: 'statement',
              NILPostId,
              videoId: item?._id,
            },
          });
        },
        uri: videoSource || ' ',
      },
    }];

    return (
      <CardContainer key={`video_${item?._id}_at_${index}`} lastChild={index === applicationService?.results?.length - 1}>
        <Card
          variant="videoSquareGroup"
          data={TALENT_RESULT}
          viewport={viewport}
        />
      </CardContainer>
    );
  });

  const _renderLeftContent = (): React$Node => (
    <>
      {_renderCards()}
      <InView as="div" onChange={_onPaginate}>
        {!applicationService?.isLastPage && (
          <SpinnerWrapper>
            <LoadingSpinner />
          </SpinnerWrapper>
        )}
      </InView>
    </>
  );

  return (
    <>
      <Helmet>
        <title>
          NIL Applications | ZIYNX
        </title>
      </Helmet>
      <ContentContainer isVertical offsetTop={(isMobileSmall || viewport === 'mobile') ? 86 : 96}>
        <PageTemplate
          alwaysShowRightSlot
          leftSlot={_renderLeftSlot()}
          leftContent={_renderLeftContent()}
          rightSlotMarginLeft="40px"
        />
      </ContentContainer>
    </>
  );
};

export default (withAppManager(NILApplicationScreen): React$ComponentType<ManagedProps<Props>>);
